import { ReactNode } from "react";
import { Box, HStack, Text } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { DiffLine, diffLines, diffWords } from "utils/text";

export interface TextDiffProps {
  /** Text before the change. */
  before: string;
  /** Text after the change. */
  after: string;
  /** Max height of the scrollable diff body. */
  maxH?: string;
}

const LINE_BG = {
  added: "var(--diff-added-bg, rgba(34, 197, 94, 0.15))",
  removed: "var(--diff-removed-bg, rgba(239, 68, 68, 0.15))",
} as const;

const WORD_BG = {
  added: "var(--diff-added-word-bg, rgba(34, 197, 94, 0.4))",
  removed: "var(--diff-removed-word-bg, rgba(239, 68, 68, 0.4))",
} as const;

const SIGN = { same: " ", added: "+", removed: "−" } as const;

type Row =
  | { kind: "same"; text: string }
  | { kind: "added" | "removed"; text: string; words?: DiffLine[] };

/**
 * Pairs each run of removed lines with the run of added lines right after it, so a line that was
 * edited (rather than deleted or inserted) gets word-level highlights showing exactly what changed.
 */
const buildRows = (before: string, after: string): Row[] => {
  const lines = diffLines(before, after);
  const rows: Row[] = [];
  let i = 0;
  while (i < lines.length) {
    if (lines[i].kind === "same") {
      rows.push({ kind: "same", text: lines[i].text });
      i++;
      continue;
    }
    const removed: string[] = [];
    const added: string[] = [];
    while (i < lines.length && lines[i].kind === "removed")
      removed.push(lines[i++].text);
    while (i < lines.length && lines[i].kind === "added")
      added.push(lines[i++].text);
    const paired = Math.min(removed.length, added.length);
    removed.forEach((text, k) =>
      rows.push({
        kind: "removed",
        text,
        words: k < paired ? diffWords(text, added[k]) : undefined,
      }),
    );
    added.forEach((text, k) =>
      rows.push({
        kind: "added",
        text,
        words: k < paired ? diffWords(removed[k], text) : undefined,
      }),
    );
  }
  return rows;
};

/**
 * Unified, single-column diff of two texts: removed lines red with "−", added lines green with "+",
 * unchanged lines plain. Inside an edited line the changed words are highlighted more strongly.
 * Line-based (LCS) — sized for prompts and policies, not source files.
 */
const TextDiff = ({ before, after, maxH = "360px" }: TextDiffProps) => {
  const { t } = useTranslation();
  const rows = buildRows(before, after);
  const changed = rows.some((r) => r.kind !== "same");

  const renderContent = (row: Row): ReactNode => {
    if (row.kind === "same" || !row.words)
      return row.text.length > 0 ? row.text : " ";
    // Show this side's tokens only; highlight the ones unique to this side.
    return row.words
      .filter((w) => w.kind === "same" || w.kind === row.kind)
      .map((w, k) =>
        w.kind === "same" ? (
          <span key={k}>{w.text}</span>
        ) : (
          <Box as="span" key={k} bg={WORD_BG[row.kind]} borderRadius="2px">
            {w.text}
          </Box>
        ),
      );
  };

  return (
    <Box
      borderWidth="1px"
      borderColor="var(--code-block-border)"
      borderRadius="6px"
      bg="var(--code-block-bg)"
      overflow="hidden"
    >
      <HStack
        gap={4}
        px={3}
        py={1}
        borderBottomWidth="1px"
        borderColor="var(--code-block-border)"
      >
        <HStack gap={1}>
          <Box w="10px" h="10px" borderRadius="2px" bg={WORD_BG.removed} />
          <Text fontSize="xs" color="var(--text-secondary-color)">
            {t("app.common.diff.removed")}
          </Text>
        </HStack>
        <HStack gap={1}>
          <Box w="10px" h="10px" borderRadius="2px" bg={WORD_BG.added} />
          <Text fontSize="xs" color="var(--text-secondary-color)">
            {t("app.common.diff.added")}
          </Text>
        </HStack>
      </HStack>
      <Box py={2} maxH={maxH} overflowY="auto">
        {!changed ? (
          <Text fontSize="xs" px={3} color="var(--text-secondary-color)">
            {t("app.common.diff.noChanges")}
          </Text>
        ) : (
          rows.map((row, i) => (
            <HStack
              key={i}
              align="flex-start"
              gap={0}
              bg={row.kind === "same" ? undefined : LINE_BG[row.kind]}
            >
              <Text
                as="span"
                fontSize="xs"
                fontFamily="mono"
                w="20px"
                flexShrink={0}
                textAlign="center"
                userSelect="none"
                color="var(--text-secondary-color)"
              >
                {SIGN[row.kind]}
              </Text>
              <Text
                fontSize="xs"
                fontFamily="mono"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
                color="var(--text-primary)"
                pr={3}
                flex={1}
                minW={0}
              >
                {renderContent(row)}
              </Text>
            </HStack>
          ))
        )}
      </Box>
    </Box>
  );
};

export default TextDiff;
